import { FC, useEffect, useMemo, useState } from 'react';
import {
  Alert,
  Autocomplete,
  Box,
  Button,
  CircularProgress,
  Grid,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  Stack,
  TextField,
  Typography,
  useTheme,
} from '@mui/material';
import { Cancel, CheckCircle } from '@mui/icons-material';

import {
  type Cutsheet,
  useCutsheetCapabilityManyQuery,
  useCutsheetMarkConvertedMutation,
  useCutsheetQuestionnaireManyQuery,
} from 'lib/graphql';

import {
  AdvancedViewStep,
  QuestionnaireResponseProvider,
  useQuestionnaireResponse,
} from './questionnaire-wizard';
import { useCatalogState } from '../hooks/use-catalog-state-graphql';
import type { CutsheetCatalog, SpecificationOption } from '../types';

const normalize = (s: string | null | undefined): string =>
  (s ?? '').toLowerCase().replace(/\s+/g, ' ').trim();

interface ConvertLegacyCutsheetModalProps {
  cutsheet: Cutsheet;
  onClose: () => void;
  onConverted?: (responseId: string) => void;
}

interface ItemStatus {
  key: string;
  label: string;
  detail?: string;
  mapped: boolean;
}

interface MappingEntry {
  inspectionLevel: string;
  questionnaireId: string;
  cutMappings?: Array<{ oldCutId: string; newOptionId: string } | null> | null;
  trimMappings?: Array<{
    oldTrimName: string;
    newOptionId: string;
  } | null> | null;
  extraMappings?: Array<{
    oldExtraName: string;
    newSpecificationId: string;
    newOptionValue: string;
  } | null> | null;
  specMappings?: Array<{
    oldSpecName: string;
    newSpecificationId: string;
  } | null> | null;
}

/**
 * Modal for converting a legacy Cutsheet into a new-format
 * CutsheetQuestionnaireResponse using the capability set's
 * legacyInspectionLevelMap.  Left panel shows coverage (✓/✗ per item),
 * right panel is the editable AdvancedViewStep seeded with mapped items.
 * @param root0
 * @param root0.cutsheet
 * @param root0.onClose
 * @param root0.onConverted
 */
export const ConvertLegacyCutsheetModal: FC<
  ConvertLegacyCutsheetModalProps
> = ({ cutsheet, onClose, onConverted }) => {
  const vendorId = cutsheet.vendor?._id;
  const species = cutsheet.animalSpecies as string | undefined;
  const inspectionLevel = (cutsheet.inspectionLevels ?? [])[0] as
    | string
    | undefined;

  // ── Resolve capability ──────────────────────────────────────────────────
  const {
    data: capabilityData,
    loading: capabilitiesLoading,
    error: capabilitiesError,
  } = useCutsheetCapabilityManyQuery({
    variables: { vendorId: vendorId!, isActive: true },
    skip: !vendorId,
  });

  const matchingCapabilities = useMemo(
    () =>
      (capabilityData?.CutsheetCapabilityByVendorAndAnimal ?? [])
        .filter((c): c is NonNullable<typeof c> => Boolean(c))
        .filter((c) => (c.species as string) === species),
    [capabilityData, species],
  );

  const [selectedCapabilityId, setSelectedCapabilityId] = useState<string>('');

  useEffect(() => {
    if (!selectedCapabilityId && matchingCapabilities.length > 0) {
      setSelectedCapabilityId(matchingCapabilities[0]._id);
    }
  }, [matchingCapabilities, selectedCapabilityId]);

  const capability = useMemo(
    () => matchingCapabilities.find((c) => c._id === selectedCapabilityId),
    [matchingCapabilities, selectedCapabilityId],
  );

  // ── Resolve legacy mapping entry for this inspection level ─────────────
  const mappingEntry = useMemo<MappingEntry | undefined>(() => {
    const entries = (capability?.legacyInspectionLevelMap ??
      []) as Array<MappingEntry | null>;
    return entries
      .filter((e): e is MappingEntry => Boolean(e))
      .find((e) => e.inspectionLevel === inspectionLevel);
  }, [capability, inspectionLevel]);

  // ── Load questionnaires for the chosen capability ───────────────────────
  const { data: questionnaireData, loading: questionnairesLoading } =
    useCutsheetQuestionnaireManyQuery({
      variables: { capabilityId: selectedCapabilityId },
      skip: !selectedCapabilityId,
    });

  const availableQuestionnaires = useMemo(
    () =>
      (questionnaireData?.cutsheetQuestionnairesByVendorAndAnimal ?? []).filter(
        (q): q is NonNullable<typeof q> => Boolean(q),
      ),
    [questionnaireData],
  );

  // User-selected questionnaire. Defaults to the mapping entry's questionnaire
  // (when present) or the first published questionnaire on the capability, but
  // the user can always override via the Autocomplete.
  const [userSelectedQuestionnaireId, setUserSelectedQuestionnaireId] =
    useState<string>('');

  // Reset the user selection whenever the capability changes.
  useEffect(() => {
    setUserSelectedQuestionnaireId('');
  }, [selectedCapabilityId]);

  // Seed the selection once questionnaires are loaded: prefer the mapping
  // entry's questionnaire, then any published questionnaire, then the first.
  useEffect(() => {
    if (userSelectedQuestionnaireId) return;
    if (availableQuestionnaires.length === 0) return;
    const mapped = mappingEntry
      ? availableQuestionnaires.find(
          (q) => q._id === mappingEntry.questionnaireId,
        )
      : undefined;
    const published = availableQuestionnaires.find(
      (q) => (q as { status?: string }).status === 'published',
    );
    const fallback = mapped ?? published ?? availableQuestionnaires[0];
    if (fallback?._id) setUserSelectedQuestionnaireId(fallback._id);
  }, [mappingEntry, userSelectedQuestionnaireId, availableQuestionnaires]);

  const effectiveQuestionnaireId = userSelectedQuestionnaireId;

  const questionnaire = useMemo(
    () =>
      availableQuestionnaires.find((q) => q._id === effectiveQuestionnaireId) ??
      null,
    [availableQuestionnaires, effectiveQuestionnaireId],
  );

  // ── Build catalog for the advanced view ─────────────────────────────────
  const { catalog, loading: catalogLoading } = useCatalogState({
    vendorId: vendorId ?? '',
    capabilityId: selectedCapabilityId,
  });

  const [markConverted] = useCutsheetMarkConvertedMutation();

  // ── Synthesize a mapping from catalog title matches when the capability
  //    doesn't have a legacyInspectionLevelMap entry for this inspection
  //    level. mappingEntry wins when present — this is just the fallback.
  const nameMatchedMappings = useMemo<MappingEntry | null>(() => {
    if (!effectiveQuestionnaireId) return null;
    if (!catalog) return null;

    const catalogCatalog = catalog as CutsheetCatalog;

    const cutMappings = (cutsheet.primalCuts ?? [])
      .filter((c): c is NonNullable<typeof c> => Boolean(c))
      .map((c) => {
        const target = normalize(c.name);
        if (!target) return null;
        const match = catalogCatalog.cuts.find(
          (cc) => normalize(cc.title) === target,
        );
        return match ? { oldCutId: c._id, newOptionId: match.id } : null;
      })
      .filter((m): m is NonNullable<typeof m> => Boolean(m));

    const trimMappings = (cutsheet.trim ?? [])
      .filter(
        (t): t is NonNullable<typeof t> => Boolean(t) && Boolean(t?.isActive),
      )
      .map((t) => {
        const target = normalize(t.name);
        if (!target) return null;
        const match = catalogCatalog.trim.find(
          (tt) => normalize(tt.title) === target,
        );
        return match && t.name
          ? { oldTrimName: t.name, newOptionId: match.id }
          : null;
      })
      .filter((m): m is NonNullable<typeof m> => Boolean(m));

    const specMappings = Object.keys(cutsheet.selectedSpecifications ?? {})
      .map((name) => {
        const target = normalize(name);
        if (!target) return null;
        const match = catalogCatalog.specifications.find(
          (s) => normalize(s.title) === target,
        );
        return match
          ? { oldSpecName: name, newSpecificationId: match.id }
          : null;
      })
      .filter((m): m is NonNullable<typeof m> => Boolean(m));

    const extraMappings: NonNullable<MappingEntry['extraMappings']> = [];
    const activeExtras = (cutsheet.extras ?? []).filter(
      (e): e is NonNullable<typeof e> => Boolean(e) && Boolean(e?.isActive),
    );
    for (const extra of activeExtras) {
      const extraName = extra.name;
      if (!extraName) continue;
      const target = normalize(extraName);

      let matched: { specId: string; optionValue: string } | null = null;
      for (const spec of catalogCatalog.specifications) {
        if (!spec.options) continue;
        const options = spec.options as Array<string | SpecificationOption>;
        for (const opt of options) {
          if (typeof opt === 'string') {
            if (normalize(opt) === target) {
              matched = { specId: spec.id, optionValue: opt };
              break;
            }
          } else {
            const candidates = [opt.value, opt.label, opt.title];
            const hit = candidates.find((c) => normalize(c) === target);
            if (hit) {
              matched = { specId: spec.id, optionValue: opt.value ?? hit };
              break;
            }
          }
        }
        if (matched) break;
      }
      if (matched) {
        extraMappings.push({
          oldExtraName: extraName,
          newSpecificationId: matched.specId,
          newOptionValue: matched.optionValue,
        });
      }
    }

    return {
      inspectionLevel: inspectionLevel ?? '',
      questionnaireId: effectiveQuestionnaireId,
      cutMappings,
      trimMappings,
      extraMappings,
      specMappings,
    };
  }, [catalog, cutsheet, inspectionLevel, effectiveQuestionnaireId]);

  // Only honor the capability's mapping entry when the user hasn't picked a
  // different questionnaire — the entry's option IDs reference options inside
  // its own questionnaire and are invalid elsewhere. When we do use it, any
  // legacy items it doesn't cover are filled in from the name-matched set so
  // the operator isn't forced to hand-pick every gap.
  const usingMappingEntry = Boolean(
    mappingEntry && mappingEntry.questionnaireId === effectiveQuestionnaireId,
  );

  const effectiveMappings = useMemo<MappingEntry | undefined>(() => {
    if (!usingMappingEntry || !mappingEntry) {
      return nameMatchedMappings ?? undefined;
    }

    const clean = <T,>(xs: Array<T | null> | null | undefined): T[] =>
      (xs ?? []).filter((x): x is T => Boolean(x));

    const entryCuts = clean(mappingEntry.cutMappings);
    const entryTrims = clean(mappingEntry.trimMappings);
    const entryExtras = clean(mappingEntry.extraMappings);
    const entrySpecs = clean(mappingEntry.specMappings);

    const nameCuts = clean(nameMatchedMappings?.cutMappings);
    const nameTrims = clean(nameMatchedMappings?.trimMappings);
    const nameExtras = clean(nameMatchedMappings?.extraMappings);
    const nameSpecs = clean(nameMatchedMappings?.specMappings);

    const cutKeys = new Set(entryCuts.map((m) => m.oldCutId));
    const trimKeys = new Set(entryTrims.map((m) => m.oldTrimName));
    const extraKeys = new Set(entryExtras.map((m) => m.oldExtraName));
    const specKeys = new Set(entrySpecs.map((m) => m.oldSpecName));

    return {
      inspectionLevel: mappingEntry.inspectionLevel,
      questionnaireId: mappingEntry.questionnaireId,
      cutMappings: [
        ...entryCuts,
        ...nameCuts.filter((m) => !cutKeys.has(m.oldCutId)),
      ],
      trimMappings: [
        ...entryTrims,
        ...nameTrims.filter((m) => !trimKeys.has(m.oldTrimName)),
      ],
      extraMappings: [
        ...entryExtras,
        ...nameExtras.filter((m) => !extraKeys.has(m.oldExtraName)),
      ],
      specMappings: [
        ...entrySpecs,
        ...nameSpecs.filter((m) => !specKeys.has(m.oldSpecName)),
      ],
    };
  }, [usingMappingEntry, mappingEntry, nameMatchedMappings]);

  // ── Compute per-item status for the left panel ──────────────────────────
  const cutStatuses = useMemo<ItemStatus[]>(() => {
    const mapped = new Map<string, string>(
      (effectiveMappings?.cutMappings ?? [])
        .filter((m): m is NonNullable<typeof m> => Boolean(m))
        .map((m) => [m.oldCutId, m.newOptionId]),
    );
    return (cutsheet.primalCuts ?? [])
      .filter((c): c is NonNullable<typeof c> => Boolean(c))
      .map((c) => ({
        key: c._id,
        label: c.name ?? 'Unnamed cut',
        mapped: mapped.has(c._id),
      }));
  }, [cutsheet.primalCuts, effectiveMappings]);

  const trimStatuses = useMemo<ItemStatus[]>(() => {
    const mapped = new Map<string, string>(
      (effectiveMappings?.trimMappings ?? [])
        .filter((m): m is NonNullable<typeof m> => Boolean(m))
        .map((m) => [m.oldTrimName, m.newOptionId]),
    );
    const activeTrims = (cutsheet.trim ?? []).filter(
      (t): t is NonNullable<typeof t> => Boolean(t) && Boolean(t?.isActive),
    );
    const isPercentage =
      activeTrims[0]?.trimSelection === 'allowSelectionByPercentage';
    return activeTrims.map((t) => {
      const parts: string[] = [];
      if (isPercentage) {
        if (t.quantity != null) parts.push(`${t.quantity}%`);
      } else {
        if (t.rank != null) parts.push(`Rank ${t.rank}`);
        if (t.quantity != null) parts.push(`${t.quantity} lb`);
      }
      return {
        key: `trim-${t.name}`,
        label: t.name ?? 'Unnamed trim',
        detail: parts.length > 0 ? parts.join(' · ') : undefined,
        mapped: mapped.has(t.name ?? ''),
      };
    });
  }, [cutsheet.trim, effectiveMappings]);

  const extraStatuses = useMemo<ItemStatus[]>(() => {
    const mapped = new Map<string, { specId: string; value: string }>(
      (effectiveMappings?.extraMappings ?? [])
        .filter((m): m is NonNullable<typeof m> => Boolean(m))
        .map((m) => [
          m.oldExtraName,
          { specId: m.newSpecificationId, value: m.newOptionValue },
        ]),
    );
    return (cutsheet.extras ?? [])
      .filter(
        (e): e is NonNullable<typeof e> => Boolean(e) && Boolean(e?.isActive),
      )
      .map((e) => {
        const parts: string[] = [];
        if (e.quantity != null && e.quantity > 0) parts.push(`${e.quantity}`);
        if (e.type === 'pricedPerPound' && e.pricePerPound != null) {
          parts.push(`$${e.pricePerPound}/lb`);
        } else if (e.type === 'flatFee' && e.fee != null) {
          parts.push(`$${e.fee} flat`);
        }
        return {
          key: `extra-${e.name}`,
          label: e.name ?? 'Unnamed extra',
          detail: parts.length > 0 ? parts.join(' · ') : 'Selected',
          mapped: mapped.has(e.name ?? ''),
        };
      });
  }, [cutsheet.extras, effectiveMappings]);

  const specStatuses = useMemo<ItemStatus[]>(() => {
    const mapped = new Map<string, string>(
      (effectiveMappings?.specMappings ?? [])
        .filter((m): m is NonNullable<typeof m> => Boolean(m))
        .map((m) => [m.oldSpecName, m.newSpecificationId]),
    );
    const answers = (cutsheet.selectedSpecifications ?? {}) as Record<
      string,
      unknown
    >;
    return Object.entries(answers)
      .filter(
        ([, value]) => value !== null && value !== undefined && value !== '',
      )
      .map(([name, value]) => ({
        key: `spec-${name}`,
        label: name,
        detail: String(value),
        mapped: mapped.has(name),
      }));
  }, [cutsheet.selectedSpecifications, effectiveMappings]);

  const totalCount =
    cutStatuses.length +
    trimStatuses.length +
    extraStatuses.length +
    specStatuses.length;
  const mappedCount = [
    ...cutStatuses,
    ...trimStatuses,
    ...extraStatuses,
    ...specStatuses,
  ].filter((s) => s.mapped).length;

  // ── Build defaultSelections for the provider ────────────────────────────
  // The provider reads `questionnaire.id` / `vendorId` (domain shape), but
  // the GQL fragment returns `_id` / `vendor`. Transform before passing.
  const seededQuestionnaire = useMemo(() => {
    if (!questionnaire) return null;
    const gql = questionnaire as unknown as {
      _id: string;
      vendor: string;
      capabilityId?: string;
      name: string;
      description?: string;
      questions?: unknown[];
      pricingOverrides?: unknown;
      specOptionPricingOverrides?: unknown;
      status?: string;
      createdAt?: string;
      updatedAt?: string;
      printSettings?: unknown;
      useCustomPrintSettings?: boolean;
    };

    const cutMap = new Map(
      (effectiveMappings?.cutMappings ?? [])
        .filter((m): m is NonNullable<typeof m> => Boolean(m))
        .map((m) => [m.oldCutId, m.newOptionId]),
    );
    const trimMap = new Map(
      (effectiveMappings?.trimMappings ?? [])
        .filter((m): m is NonNullable<typeof m> => Boolean(m))
        .map((m) => [m.oldTrimName, m.newOptionId]),
    );
    const extraMap = new Map(
      (effectiveMappings?.extraMappings ?? [])
        .filter((m): m is NonNullable<typeof m> => Boolean(m))
        .map((m) => [
          m.oldExtraName,
          { specId: m.newSpecificationId, value: m.newOptionValue },
        ]),
    );
    const specMap = new Map(
      (effectiveMappings?.specMappings ?? [])
        .filter((m): m is NonNullable<typeof m> => Boolean(m))
        .map((m) => [m.oldSpecName, m.newSpecificationId]),
    );

    const selectedCuts = (cutsheet.primalCuts ?? [])
      .filter((c): c is NonNullable<typeof c> => Boolean(c))
      .map((c) => cutMap.get(c._id))
      .filter((id): id is string => Boolean(id))
      .map((optionId) => ({ optionId }));

    // Trim — preserve ranked vs percentage from the legacy cutsheet
    const activeTrims = (cutsheet.trim ?? []).filter(
      (t): t is NonNullable<typeof t> => Boolean(t) && Boolean(t?.isActive),
    );
    const trimSelectionMode = activeTrims[0]?.trimSelection;
    const isPercentage = trimSelectionMode === 'allowSelectionByPercentage';
    const mappedTrims = activeTrims
      .map((t) => {
        const optionId = trimMap.get(t.name ?? '');
        if (!optionId) return null;
        return isPercentage
          ? { optionId, percentage: t.quantity ?? 0 }
          : { optionId, rank: t.rank ?? 0, quantity: t.quantity ?? 0 };
      })
      .filter((x): x is NonNullable<typeof x> => Boolean(x));

    const trimSelections =
      mappedTrims.length > 0
        ? {
            type: isPercentage
              ? ('selection_by_percentage' as const)
              : ('ranked' as const),
            trim: mappedTrims,
          }
        : undefined;

    // Extras → set the extras spec's multiselect value to the mapped option value.
    // Group by specId (all active extras share one "Add-Ons & Extras" spec).
    const extraBySpec = new Map<string, string[]>();
    for (const extra of cutsheet.extras ?? []) {
      if (!extra?.isActive || !extra.name) continue;
      const mapping = extraMap.get(extra.name);
      if (!mapping) continue;
      const arr = extraBySpec.get(mapping.specId) ?? [];
      arr.push(mapping.value);
      extraBySpec.set(mapping.specId, arr);
    }

    // Specs — cutsheet.selectedSpecifications is a Map<name, value>.
    const specAnswers = (cutsheet.selectedSpecifications ?? {}) as Record<
      string,
      string | number
    >;
    const mappedSpecs: Array<{
      specification: string;
      type: 'text' | 'number' | 'select' | 'multiselect';
      value: string;
    }> = [];
    for (const [name, value] of Object.entries(specAnswers)) {
      const newSpecId = specMap.get(name);
      if (!newSpecId) continue;
      mappedSpecs.push({
        specification: newSpecId,
        type: 'text',
        value: String(value),
      });
    }
    for (const [specId, values] of extraBySpec.entries()) {
      mappedSpecs.push({
        specification: specId,
        type: 'multiselect',
        value: JSON.stringify(values),
      });
    }

    return {
      id: gql._id,
      vendorId: String(gql.vendor ?? ''),
      capabilityId: gql.capabilityId ?? '',
      name: gql.name,
      description: gql.description,
      questions: (gql.questions ?? []) as unknown as never,
      pricingOverrides: gql.pricingOverrides as never,
      specOptionPricingOverrides: gql.specOptionPricingOverrides as never,
      status:
        (gql.status as 'draft' | 'published' | 'archived' | undefined) ??
        'draft',
      useCustomPrintSettings: gql.useCustomPrintSettings,
      printSettings: gql.printSettings as never,
      createdAt: gql.createdAt ? new Date(gql.createdAt) : new Date(),
      updatedAt: gql.updatedAt ? new Date(gql.updatedAt) : new Date(),
      defaultSelections: {
        selectedCuts,
        trimSelections,
        specifications: mappedSpecs,
      },
    };
  }, [questionnaire, effectiveMappings, cutsheet]);

  // ── Loading / error states ──────────────────────────────────────────────
  const loading =
    capabilitiesLoading || questionnairesLoading || catalogLoading;

  if (!vendorId || !species) {
    return (
      <Alert severity="error">
        This cutsheet is missing vendor or species information; cannot convert.
      </Alert>
    );
  }

  if (loading) {
    return (
      <Box sx={{ textAlign: 'center', py: 6 }}>
        <CircularProgress />
        <Typography variant="body2" color="text.secondary" sx={{ mt: 2 }}>
          Loading capability set and mapping rules…
        </Typography>
      </Box>
    );
  }

  if (capabilitiesError) {
    return (
      <Alert severity="error">
        Failed to load capabilities: {capabilitiesError.message}
      </Alert>
    );
  }

  if (matchingCapabilities.length === 0) {
    return (
      <Alert severity="warning">
        No active capability set found for <strong>{species}</strong>. Create
        one before converting this cutsheet.
      </Alert>
    );
  }

  if (availableQuestionnaires.length === 0) {
    return (
      <Alert severity="warning">
        No questionnaires exist on capability{' '}
        <strong>{capability?.name}</strong>. Create one before converting this
        cutsheet.
      </Alert>
    );
  }

  if (!questionnaire || !seededQuestionnaire) {
    return (
      <Alert severity="warning">Select a Cutsheet Form to continue.</Alert>
    );
  }

  return (
    <QuestionnaireResponseProvider
      questionnaire={seededQuestionnaire}
      catalog={catalog}
      onClose={onClose}
      onSave={async (responseId) => {
        try {
          await markConverted({
            variables: {
              cutsheetId: cutsheet._id,
              responseId,
            },
          });
        } catch (err) {
          // Non-fatal: the response was created; we just couldn't record the
          // back-link.  Surface in console but continue.
          // eslint-disable-next-line no-console
          console.error('Failed to mark cutsheet as converted', err);
        }
        onConverted?.(responseId);
        onClose();
      }}
      fixedCutsheetType={
        cutsheet.type as 'user' | 'vendor' | 'orphan' | undefined
      }
      fixedUserId={
        cutsheet.type === 'user' ? (cutsheet.user?._id ?? undefined) : undefined
      }
    >
      <Stack spacing={2}>
        <Stack direction={{ xs: 'column', md: 'row' }} spacing={2}>
          {matchingCapabilities.length > 1 && (
            <Autocomplete
              size="small"
              disableClearable
              options={matchingCapabilities}
              getOptionLabel={(c) => c.name ?? 'Unnamed'}
              value={capability ?? matchingCapabilities[0]}
              onChange={(_, val) => setSelectedCapabilityId(val?._id ?? '')}
              renderInput={(params) => (
                <TextField {...params} label="Capability set" />
              )}
              sx={{ flex: 1 }}
            />
          )}
          {availableQuestionnaires.length > 0 && (
            <Autocomplete
              size="small"
              disableClearable
              options={availableQuestionnaires}
              getOptionLabel={(q) => (q as { name?: string }).name ?? 'Unnamed'}
              value={questionnaire ?? availableQuestionnaires[0]}
              onChange={(_, val) =>
                setUserSelectedQuestionnaireId(
                  (val as { _id: string })?._id ?? '',
                )
              }
              renderInput={(params) => (
                <TextField {...params} label="Cutsheet Form" />
              )}
              sx={{ flex: 1 }}
            />
          )}
          <NameField initialName={cutsheet.name ?? ''} />
        </Stack>

        <Alert severity={usingMappingEntry ? 'info' : 'warning'}>
          Converting <strong>{cutsheet.name}</strong> into a new-format response
          on <strong>{capability?.name}</strong> / {questionnaire.name}.{' '}
          {usingMappingEntry
            ? `${mappedCount} of ${totalCount} items have mappings.`
            : `Falling back to name matching (${mappedCount} of ${totalCount} items matched).`}{' '}
          Unmapped items are highlighted on the left — adjust selections on the
          right before converting.
        </Alert>

        <Grid container spacing={2}>
          <Grid size={{ xs: 12, md: 4 }}>
            <CoverageList title="Cuts" items={cutStatuses} />
            <CoverageList title="Trims" items={trimStatuses} />
            <CoverageList title="Extras" items={extraStatuses} />
            <CoverageList title="Specifications" items={specStatuses} />
          </Grid>

          <Grid size={{ xs: 12, md: 8 }}>
            <Box
              sx={{
                borderLeft: { md: '1px solid' },
                borderColor: { md: 'divider' },
                pl: { md: 2 },
              }}
            >
              <AdvancedViewStep disableNonQuestionnaireCuts={false} />
            </Box>
          </Grid>
        </Grid>

        <Box
          sx={{
            position: 'sticky',
            bottom: '-24px',
            mx: (theme) => theme.spacing(-3),
            mb: (theme) => theme.spacing(-2),
            px: 3,
            py: 1.5,
            bgcolor: 'background.paper',
            borderTop: '1px solid',
            borderColor: 'divider',
            zIndex: 2,
          }}
        >
          <ActionBar onCancel={onClose} />
        </Box>
      </Stack>
    </QuestionnaireResponseProvider>
  );
};

// ─── Left-panel section ────────────────────────────────────────────────────

interface CoverageListProps {
  title: string;
  items: ItemStatus[];
}

const CoverageList: FC<CoverageListProps> = ({ title, items }) => {
  const theme = useTheme();
  if (items.length === 0) return null;
  return (
    <Box sx={{ mb: 2 }}>
      <Typography variant="subtitle2" fontWeight="bold" sx={{ mb: 0.5 }}>
        {title} ({items.filter((i) => i.mapped).length}/{items.length})
      </Typography>
      <List dense disablePadding>
        {items.map((item) => (
          <ListItem
            key={item.key}
            disableGutters
            sx={{
              px: 1,
              borderRadius: 1,
              backgroundColor: item.mapped
                ? 'transparent'
                : `${theme.palette.warning.light}33`,
            }}
          >
            <ListItemIcon sx={{ minWidth: 32 }}>
              {item.mapped ? (
                <CheckCircle color="success" fontSize="small" />
              ) : (
                <Cancel color="warning" fontSize="small" />
              )}
            </ListItemIcon>
            <ListItemText
              primary={item.label}
              secondary={item.detail}
              primaryTypographyProps={{
                variant: 'body2',
                sx: item.mapped ? {} : { fontWeight: 500 },
              }}
              secondaryTypographyProps={{
                variant: 'caption',
                color: 'text.secondary',
              }}
            />
          </ListItem>
        ))}
      </List>
    </Box>
  );
};

// ─── Top-row name input (reads provider state) ─────────────────────────────

interface NameFieldProps {
  initialName: string;
}

const NameField: FC<NameFieldProps> = ({ initialName }) => {
  const { handleCutsheetNameChange, cutsheetName } = useQuestionnaireResponse();

  // Seed the provider name once on mount so handleSave passes the
  // no-empty-name check without the operator having to retype.
  useEffect(() => {
    if (!cutsheetName) handleCutsheetNameChange(initialName);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <TextField
      size="small"
      label="New cutsheet name"
      value={cutsheetName || ''}
      onChange={(e) => handleCutsheetNameChange(e.target.value)}
      sx={{ flex: 1 }}
    />
  );
};

// ─── Footer action bar (reads provider state) ──────────────────────────────

interface ActionBarProps {
  onCancel: () => void;
}

const ActionBar: FC<ActionBarProps> = ({ onCancel }) => {
  const { handleSave, isSaving, selectedCuts, cutsheetName } =
    useQuestionnaireResponse();
  const [error, setError] = useState<string | null>(null);

  const handleClick = async () => {
    setError(null);
    if (!cutsheetName.trim()) {
      setError('Please enter a name before converting.');
      return;
    }
    try {
      await handleSave();
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Failed to convert cutsheet.');
    }
  };

  return (
    <Stack spacing={1}>
      {error && <Alert severity="error">{error}</Alert>}
      <Stack direction="row" spacing={2} alignItems="center">
        <Typography variant="body2" color="text.secondary">
          {selectedCuts.length} cut{selectedCuts.length === 1 ? '' : 's'}{' '}
          selected
        </Typography>
        <Box sx={{ flex: 1 }} />
        <Button onClick={onCancel} disabled={isSaving}>
          Cancel
        </Button>
        <Button variant="contained" onClick={handleClick} disabled={isSaving}>
          {isSaving ? 'Converting…' : 'Convert'}
        </Button>
      </Stack>
    </Stack>
  );
};
